import * as React from 'react';
import { useHistory } from 'react-router-dom';

export const LEAVE_WITHOUT_SAVING_MESSAGE = 'Are you sure you want to leave without saving changes?';

/**
 * Asks before any in-app navigation while `when` is true (CP-8409), through
 * the router's getUserConfirmation like react-router's <Prompt>.
 *
 * - `isIgnored(nextLocation)` lets navigations through without asking, e.g.
 *   query-string-only replaces that open and close drawers on the same page.
 * - The returned `leaveWithoutPrompt(navigate)` runs a navigation that must not
 *   ask, such as the redirect after a successful save. The skip lasts until
 *   `when` next turns true.
 *
 * history v4 keeps one blocker at a time, so call this once per page.
 * Dismissals that do not navigate (a drawer closed by a state change) still
 * need their own window.confirm(LEAVE_WITHOUT_SAVING_MESSAGE).
 */
const useLeavePrompt = (when, { message = LEAVE_WITHOUT_SAVING_MESSAGE, isIgnored } = {}) => {
    const history = useHistory();
    const skipRef = React.useRef(false);
    const isIgnoredRef = React.useRef(isIgnored);
    isIgnoredRef.current = isIgnored;

    React.useEffect(() => {
        if (!when) return undefined;
        skipRef.current = false;
        return history.block((nextLocation) => {
            if (skipRef.current || isIgnoredRef.current?.(nextLocation)) return undefined;
            return message;
        });
    }, [when, message, history]);

    return React.useCallback((navigate) => {
        skipRef.current = true;
        navigate();
    }, []);
};

export default useLeavePrompt;
